/**
 * Admin · Payments health (Stripe + PayPal).
 *
 * Probes both processors in real time and reports green / yellow / red
 * for each. Also shows the last 24h of paid vs failed orders and the
 * most recent failure for fast triage. Open this page the second a
 * seller says "checkout broke" — within 2 seconds you'll know whether
 * the cause is keys, outage, network, or just a single bad card.
 */
import React, { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowsClockwiseIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
} from "@phosphor-icons/react";

const VERDICT_STYLES = {
  green: { chip: "bg-emerald-500/15 text-emerald-700 border-emerald-300", dot: "bg-emerald-500", label: "HEALTHY" },
  yellow: { chip: "bg-amber-500/15 text-amber-800 border-amber-300", dot: "bg-amber-500", label: "DEGRADED" },
  red: { chip: "bg-rose-500/15 text-rose-700 border-rose-300", dot: "bg-rose-500", label: "BROKEN" },
};

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-2 border-b border-zinc-200 last:border-0">
      <span className="text-xs uppercase tracking-wider font-bold text-zinc-500">
        {label}
      </span>
      <span className="text-sm font-mono text-zinc-900 break-all text-right ml-3">
        {String(value ?? "—")}
      </span>
    </div>
  );
}

function ProviderCard({ icon: Icon, title, provider }) {
  const style = VERDICT_STYLES[provider?.verdict || "red"] || VERDICT_STYLES.red;
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-200">
        <div className="h-10 w-10 rounded-xl bg-[#1D78AB]/10 border border-[#1D78AB]/30 grid place-items-center">
          <Icon size={20} weight="fill" className="text-[#1D78AB]" />
        </div>
        <h2 className="font-display font-black text-lg tracking-tight">{title}</h2>
        <span
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold ${style.chip}`}
          data-testid={`payments-${title.toLowerCase()}-verdict`}
        >
          <span className={`h-2 w-2 rounded-full ${style.dot} ${provider?.verdict === "green" ? "" : "animate-pulse"}`} />
          {style.label}
        </span>
      </div>
      <div className="px-5 py-4">
        <p className="text-sm font-bold leading-snug" data-testid={`payments-${title.toLowerCase()}-message`}>
          {provider?.verdict_message || "—"}
        </p>
        <div className="mt-4">
          <Row label="Configured" value={provider?.configured ? "yes" : "MISSING"} />
          {provider?.mode && <Row label="Secret key mode" value={provider.mode} />}
          {provider?.publishable_mode && <Row label="Publishable key mode" value={provider.publishable_mode} />}
          {provider?.key_mode_mismatch && (
            <div
              className="mt-3 rounded-md border border-rose-400 bg-rose-50 p-3 text-xs text-rose-800 font-bold leading-snug"
              data-testid={`payments-${title.toLowerCase()}-key-mismatch`}
            >
              ⚠ Secret key ({provider.mode}) and publishable key ({provider.publishable_mode}) are in different modes.
              Real-card checkouts will fail with “card declined, request in test mode”. Set
              <code className="mx-1 px-1 bg-rose-100 rounded">STRIPE_PUBLISHABLE_KEY</code>
              to a <code className="mx-1 px-1 bg-rose-100 rounded">pk_{provider.mode}_…</code> value in your deployment env, then redeploy.
            </div>
          )}
          {provider?.environment && <Row label="Environment" value={provider.environment} />}
          {provider?.api_base && <Row label="API base" value={provider.api_base} />}
        </div>
        {provider?.api && (
          <div className="mt-4">
            <h3 className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">
              Round-trip probe
            </h3>
            <Row label="HTTP status" value={provider.api.status_code} />
            <Row label="Latency" value={`${provider.api.elapsed_ms} ms`} />
            {provider.api.body_preview && (
              <pre className="mt-3 text-xs bg-zinc-50 border border-zinc-200 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all">
                {provider.api.body_preview}
              </pre>
            )}
            {provider.api.error && (
              <pre className="mt-3 text-xs bg-rose-50 border border-rose-200 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all text-rose-700">
                {provider.api.error}
              </pre>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default function AdminPaymentsHealth() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.get("/admin/payments/health");
      setData(r.data);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <>
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="admin-payments-health">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-emerald-400/15 border border-emerald-400/30 grid place-items-center">
            <CurrencyDollarIcon size={22} weight="fill" className="text-emerald-600" />
          </div>
          <div>
            <h1 className="font-display font-black text-2xl tracking-tight">
              Payments · health
            </h1>
            <p className="text-sm text-zinc-500">
              Live probes of Stripe + PayPal plus a 24-hour activity rollup.
            </p>
          </div>
          <div className="ml-auto">
            <Button
              variant="outline"
              onClick={refresh}
              disabled={busy}
              data-testid="payments-health-refresh"
              className="gap-2"
            >
              <ArrowsClockwiseIcon size={16} className={busy ? "animate-spin" : ""} />
              {busy ? "Probing…" : "Re-check"}
            </Button>
          </div>
        </div>

        {err && (
          <div className="mt-6 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="payments-health-error">
            {err}
          </div>
        )}

        {data && (
          <>
            <div className="mt-6 grid gap-5">
              <ProviderCard icon={CreditCardIcon} title="Stripe" provider={data.stripe} />
              <ProviderCard icon={CurrencyDollarIcon} title="PayPal" provider={data.paypal} />
            </div>

            <section className="mt-6 rounded-2xl border border-zinc-200 bg-white p-5">
              <h2 className="font-bold text-sm uppercase tracking-wider text-zinc-500 mb-3">
                Recent activity (last 24 hours)
              </h2>
              <Row label="Paid orders" value={data.recent_activity?.paid_orders_24h ?? "—"} />
              <Row label="Failed / cancelled" value={data.recent_activity?.failed_orders_24h ?? "—"} />
              {data.recent_activity?.last_failure ? (
                <div className="mt-4">
                  <h3 className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">
                    Most recent failure
                  </h3>
                  <Row label="Session id" value={data.recent_activity.last_failure.session_id} />
                  <Row label="Status" value={data.recent_activity.last_failure.payment_status} />
                  <Row label="Provider" value={data.recent_activity.last_failure.provider} />
                  <Row label="Amount" value={`${data.recent_activity.last_failure.amount} ${data.recent_activity.last_failure.currency || ""}`} />
                  <Row label="When" value={data.recent_activity.last_failure.created_at} />
                </div>
              ) : (
                <p className="text-sm text-zinc-500 mt-3">No failures recorded yet.</p>
              )}
              {data.recent_activity?.error && (
                <pre className="mt-3 text-xs bg-rose-50 border border-rose-200 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all text-rose-700">
                  {data.recent_activity.error}
                </pre>
              )}
            </section>

            <p className="mt-6 text-xs text-zinc-500">
              Tip: if either processor reads <span className="font-bold text-rose-600">BROKEN</span>{" "}
              with "credentials rejected", re-issue the API key in the
              processor's dashboard and update the corresponding env var
              on your deployment.
            </p>
          </>
        )}
      </div>
      <Footer />
    </>
  );
}
